import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import PhoneInput, { parsePhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { API_BASE_URL } from '@/utils/env';
import { BRAND } from '@/brand';
import '../styles/authPages.css';

const LOGO_URL = 'https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/Conncct-logo.png';

const ACCT_VARS: React.CSSProperties = {
  '--cream': '#FFFDF7',
  '--accent': '#FFD54F',
  '--accent-deep': '#E6B400',
  '--on-accent': '#17263A',
  '--accent-glow': 'rgba(255,213,79,.26)',
  '--accent-tint': '#ffedc0',
  '--accent-ink': '#8a6400',
  '--line': '#E8E2D2',
  '--line-strong': '#DCD3BC',
  '--card-line': '#EFE9DA',
  '--field-bg': '#FFFDF7',
  '--ring': 'rgba(230,180,0,.28)',
  '--link': '#17263A',
} as React.CSSProperties;

const STRENGTH_COLORS = ['#D8503F', '#C9881C', '#C9881C', '#15824E'];
const STRENGTH_LABELS = ['Weak', 'Fair, add variety', 'Good', 'Strong password'];

function scorePassword(v: string) {
  let s = 0;
  if (v.length >= 8) s++;
  if (/[A-Z]/.test(v) && /[a-z]/.test(v)) s++;
  if (/\d/.test(v)) s++;
  if (/[^A-Za-z0-9]/.test(v)) s++;
  return Math.min(s, 4);
}

function AccountingSignup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pwScore, setPwScore] = useState(0);

  useEffect(() => {
    localStorage.setItem('signupUserType', 'setup');
    localStorage.setItem('activeServiceView', 'accounting');
    // Carry the plan the visitor picked on the marketing pricing page through signup,
    // so the setup/operate plan step can pre-select it. Read by SetupPlan/OperatePlan.
    (['plan', 'billing', 'flags', 'band', 'cur', 'ref'] as const).forEach(k => {
      const v = searchParams.get(k);
      if (v) localStorage.setItem(`signup_${k}`, v);
    });
  }, [searchParams]);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!firstName || !lastName || !email || !password || !phoneNumber) {
      setError('Please fill in all fields.');
      return;
    }
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }

    setLoading(true);
    try {
      let parsedCountryCode = '+971';
      let parsedNationalNumber = phoneNumber;
      try {
        const parsed = parsePhoneNumber(phoneNumber);
        if (parsed) {
          parsedCountryCode = `+${parsed.countryCallingCode}`;
          parsedNationalNumber = parsed.nationalNumber;
        }
      } catch {}

      const response = await fetch(`${API_BASE_URL}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstName, lastName, email, password, phoneNumber: parsedNationalNumber, countryCode: parsedCountryCode, brand: BRAND, referredBy: localStorage.getItem('signup_ref') || undefined })
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || 'Failed to create account');

      localStorage.setItem('user', JSON.stringify({ uid: data.user.uid, email: data.user.email, displayName: data.user.displayName, firstName, lastName }));
      localStorage.removeItem('signup_ref');
      if (data.tokens) {
        localStorage.setItem('authTokens', JSON.stringify(data.tokens));
        if (data.tokens.idToken) { localStorage.setItem('idToken', data.tokens.idToken); localStorage.setItem('token', data.tokens.idToken); }
        if (data.tokens.refreshToken) localStorage.setItem('refreshToken', data.tokens.refreshToken);
      }
      navigate('/email-verification', { replace: true, state: { email: data.user.email, firstName } });
    } catch (err: any) {
      setError(err.message === 'Failed to fetch' ? 'Cannot connect to server. Please check your internet connection.' : err.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async (credentialResponse: CredentialResponse) => {
    if (!credentialResponse?.credential) { setError('Failed to get Google credentials. Please try again.'); return; }
    setLoading(true); setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/auth/google-signin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: credentialResponse.credential, brand: BRAND, referredBy: localStorage.getItem('signup_ref') || undefined })
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || 'Google signin failed');
      if (!data.user?.uid || data.user.uid === 'error') throw new Error('Failed to get valid user ID from Google authentication');
      if (!data.tokens?.idToken) throw new Error('Failed to get authentication tokens');

      localStorage.setItem('user', JSON.stringify({ uid: data.user.uid, email: data.user.email, displayName: data.user.displayName || `${data.user.firstName} ${data.user.lastName}`, firstName: data.user.firstName, lastName: data.user.lastName, photoURL: data.user.photoURL || '', emailVerified: true }));
      localStorage.removeItem('signup_ref');
      localStorage.setItem('authTokens', JSON.stringify(data.tokens));
      localStorage.setItem('idToken', data.tokens.idToken);
      localStorage.setItem('token', data.tokens.idToken);
      localStorage.setItem('refreshToken', data.tokens.refreshToken || '');
      navigate('/accounting/select', { replace: true });
    } catch (err: any) {
      setError(err.message === 'Failed to fetch' ? 'Cannot connect to server.' : err.message || 'Failed to sign in with Google. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const EyeIcon = ({ open }: { open: boolean }) => (
    <svg viewBox="0 0 24 24">
      {open ? (
        <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/><line x1="3" y1="3" x2="21" y2="21"/></>
      ) : (
        <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></>
      )}
    </svg>
  );

  return (
    <div className="auth-shell" style={{ ...ACCT_VARS, minHeight: '100vh' } as React.CSSProperties}>
      <div className="shell">
        {/* LEFT RAIL */}
        <aside className="rail">
          <div className="rl-logo">
            <a href="/" style={{ lineHeight: 0 }}>
              <img src={LOGO_URL} alt="Conncct" style={{ height: 34, width: 'auto', filter: 'brightness(0) invert(1)' }} />
            </a>
          </div>
          <div className="rl-body">
            <span className="rl-eyebrow">Bookkeeping · VAT · Tax</span>
            <h2>The business side of your startup, <span className="hl">handled.</span></h2>
            <p className="rl-lead">Books, VAT and UAE corporate tax, filed and compliant, never a missed FTA deadline.</p>
            <div className="mock">
              <div className="mock-top"><b>Compliance</b><span className="chip">FTA</span></div>
              <div className="ck">
                <span className="d"><svg viewBox="0 0 24 24"><path d="M5 12l5 5L20 7" stroke="#15824E" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg></span>
                <span className="x"><b>VAT return Q1</b><small>Submitted to the FTA</small></span>
                <span className="st done">Done</span>
              </div>
              <div className="ck">
                <span className="d due"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8.5" stroke="#9A6B00" strokeWidth="2.2" fill="none"/><path d="M12 7v5l3 2" stroke="#9A6B00" strokeWidth="2.3" strokeLinecap="round" fill="none"/></svg></span>
                <span className="x"><b>Corporate Tax filing</b><small>9% on profit over AED 375k</small></span>
                <span className="st due">Due soon</span>
              </div>
              <div className="ck">
                <span className="d"><svg viewBox="0 0 24 24"><path d="M5 12l5 5L20 7" stroke="#15824E" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg></span>
                <span className="x"><b>Bookkeeping</b><small>187 transactions reconciled</small></span>
                <span className="st done">Synced</span>
              </div>
              <div style={{ marginTop: 14 }}>
                <div className="bar-row"><span>Investor-ready</span><b className="tnum">80%</b></div>
                <div className="bar"><i style={{ width: '80%' }}></i></div>
              </div>
            </div>
          </div>
          <div className="rl-foot">
            <svg viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            Bank-grade encryption · your data is never sold
          </div>
        </aside>

        {/* RIGHT PANE */}
        <main className="pane">
          <div className="topbar">
            <a className="top-pill" href="/accounting/login">Log in</a>
          </div>
          <div className="center">
            <section className="card">
              <span className="eyebrow">Create your account</span>
              <h1>Your back office, <span className="hl">handled</span>.</h1>
              <p className="sub">Set up your account to manage books, VAT and UAE corporate tax in one place.</p>

              {error && <div className="err-msg">{error}</div>}

              <form onSubmit={handleSignup} noValidate>
                <div className="grid2">
                  <div className="field">
                    <label htmlFor="fn">First name</label>
                    <input className="input" id="fn" type="text" placeholder="Sara" value={firstName} onChange={e => setFirstName(e.target.value)} autoComplete="given-name" />
                  </div>
                  <div className="field">
                    <label htmlFor="ln">Last name</label>
                    <input className="input" id="ln" type="text" placeholder="Al Maktoum" value={lastName} onChange={e => setLastName(e.target.value)} autoComplete="family-name" />
                  </div>
                </div>

                <div className="field">
                  <label>Phone number</label>
                  <div className="phone-wrap">
                    <PhoneInput
                      placeholder="50 123 4567"
                      value={phoneNumber}
                      onChange={(v) => setPhoneNumber(v || '')}
                      defaultCountry="AE"
                      international
                      countryCallingCodeEditable={false}
                    />
                  </div>
                </div>

                <div className="field">
                  <label htmlFor="email-acct">Email address</label>
                  <input className="input" id="email-acct" type="email" placeholder="you@company.com" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" />
                </div>

                <div className="field has-icon">
                  <label htmlFor="pw-acct">Password</label>
                  <div className="control">
                    <input className="input" id="pw-acct" type={showPassword ? 'text' : 'password'} placeholder="Create a password" value={password} onChange={e => { setPassword(e.target.value); setPwScore(scorePassword(e.target.value)); }} autoComplete="new-password" />
                    <button type="button" className="toggle" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                      <EyeIcon open={showPassword} />
                    </button>
                  </div>
                  <div className="strength" aria-hidden="true">
                    {[0,1,2,3].map(i => <i key={i} style={{ background: i < pwScore ? STRENGTH_COLORS[pwScore - 1] : undefined }}></i>)}
                  </div>
                  <div className={`hint${pwScore >= 3 && password ? ' ok' : password ? ' bad' : ''}`}>
                    {!password ? 'Use 8+ characters with a number and a symbol.' : STRENGTH_LABELS[Math.max(0, pwScore - 1)]}
                  </div>
                </div>

                <div className="field has-icon">
                  <label htmlFor="pw2-acct">Confirm password</label>
                  <div className="control">
                    <input className="input" id="pw2-acct" type={showConfirm ? 'text' : 'password'} placeholder="Re-enter password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} autoComplete="new-password" />
                    <button type="button" className="toggle" onClick={() => setShowConfirm(!showConfirm)} aria-label={showConfirm ? 'Hide password' : 'Show password'}>
                      <EyeIcon open={showConfirm} />
                    </button>
                  </div>
                  {confirmPassword && (
                    <div className={`hint ${password === confirmPassword ? 'ok' : 'bad'}`}>
                      {password === confirmPassword ? 'Passwords match' : "Passwords don't match yet"}
                    </div>
                  )}
                </div>

                <div className="actions">
                  <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>← Back</button>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Creating…' : 'Create account →'}
                  </button>
                </div>
              </form>

              <div className="divider">or continue with</div>
              <div className="google-wrap">
                <GoogleLogin
                  onSuccess={handleGoogleSignIn}
                  onError={() => setError('Google Sign-In failed. Please try again or use email/password.')}
                  text="signup_with"
                  size="large"
                  width="400"
                  theme="outline"
                  shape="rectangular"
                />
              </div>

              <p className="switch">Already have an account? <a href="/accounting/login">Log in</a></p>
              <p className="legal">By continuing you agree to our <a href="/privacy">Terms</a> &amp; <a href="/privacy">Privacy Policy</a>.</p>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}

export default AccountingSignup;
